import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiDownload, FiTrash2 } from '../../utils/icons';
import { Employee, Organization, Location, SalaryComponent } from '../../types';
import { updateEmployee, getOrganizations, getAllLocations, getEmployeeDocDownloadUrl, getEmployeeDocUploadUrl, uploadFileToS3 } from '../../services/adminService';
import { validateEmployeeForAppointmentLetter, generateAppointmentLetter } from '../../utils/appointmentLetterGenerator';
import { ValidationModal } from './ValidationModal';

interface EditEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  employee: Employee | null;
}

interface EmployeeFormData {
  // Basic Information
  name: string;
  employeeId: string;
  email: string;
  mobileNumber: string;
  dateOfBirth: string;
  gender: string;
  bloodGroup: string;
  maritalStatus: string;

  // Address Information
  permanentAddress: string;
  currentAddress: string;
  city: string;
  state: string;
  pincode: string;

  // Emergency Contact
  emergencyContactName: string;
  emergencyContactRelation: string;
  emergencyContactPhone: string;

  // Employment Details
  orgId: string;
  locationIds: string[];
  designation: string;
  department: string;
  dateOfJoining: string;
  reportingManager: string;
  employmentType: 'Full-time' | 'Part-time' | 'Contract' | 'Intern';
  status: 'active' | 'inactive' | 'terminated';
  probationPeriod: string;
  workShift: string;
  vendorId: string;

  // Salary
  salaryComponents: SalaryComponent[];

  // Identity & Banking
  aadharNumber: string;
  panNumber: string;
  bankAccountNumber: string;
  bankIfscCode: string;
  bankBranch: string;
  bankName: string;
  uanNumber: string;
  esiNumber: string;

  // Uniform Information
  shirtSize: string;
  trouserSize: string;
  shoeSize: string;

  // Documents - arrays to support multiple files
  profilePhoto: File[];
  aadharCardDoc: File[];
  panCardDoc: File[];
  addressProofDoc: File[];
  mergedDetails: File[];
}

const SALARY_COMPONENT_TYPES: SalaryComponent['component'][] = [
  'Basic',
  'DA',
  'HRA',
  'Conveyance',
  'Bonus',
  'Leave Salary',
  'Work Allowance',
  'Washing Allowance',
  'EC Policy',
  'ESIC',
  'PF',
  'Ex Gratia',
  'Statutory Deductions (In-Hand)',
];

export const EditEmployeeModal: React.FC<EditEmployeeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  employee,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [showSalarySection, setShowSalarySection] = useState(false);
  const [enabledComponents, setEnabledComponents] = useState<Set<SalaryComponent['component']>>(new Set());
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [formData, setFormData] = useState<EmployeeFormData>({
    name: '',
    employeeId: '',
    email: '',
    mobileNumber: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    maritalStatus: '',
    permanentAddress: '',
    currentAddress: '',
    city: '',
    state: '',
    pincode: '',
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    orgId: '',
    locationIds: [],
    designation: '',
    department: '',
    dateOfJoining: '',
    reportingManager: '',
    employmentType: 'Full-time',
    status: 'active',
    probationPeriod: '',
    workShift: '',
    vendorId: '',
    salaryComponents: [],
    aadharNumber: '',
    panNumber: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankBranch: '',
    bankName: '',
    uanNumber: '',
    esiNumber: '',
    shirtSize: '',
    trouserSize: '',
    shoeSize: '',
    profilePhoto: [],
    aadharCardDoc: [],
    panCardDoc: [],
    addressProofDoc: [],
    mergedDetails: [],
  });

  useEffect(() => {
    if (isOpen) {
      loadOrganizationsAndLocations();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.orgId) {
      const filtered = locations.filter((loc) => loc.orgId === parseInt(formData.orgId));
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations([]);
    }
  }, [formData.orgId, locations]);

  useEffect(() => {
    if (employee) {
      // Map employee status to form status
      const mapStatus = (status: Employee['status']): EmployeeFormData['status'] => {
        if (status === 'active' || status === 'inactive' || status === 'terminated') {
          return status;
        }
        // Default to active for onboarding statuses
        return 'active';
      };

      const salaryComps = employee.salaryComponents || [];

      // Helper function to convert ISO date to yyyy-MM-dd format
      const formatDateForInput = (isoDate: string | undefined) => {
        if (!isoDate) return '';
        try {
          const date = new Date(isoDate);
          return date.toISOString().split('T')[0];
        } catch {
          return '';
        }
      };

      setFormData({
        name: employee.name || '',
        employeeId: employee.employeeId || '',
        email: employee.email || '',
        mobileNumber: employee.mobileNumber || '',
        dateOfBirth: formatDateForInput(employee.dateOfBirth),
        gender: employee.gender || '',
        bloodGroup: employee.bloodGroup || '',
        maritalStatus: employee.maritalStatus || '',
        permanentAddress: employee.permanentAddress || '',
        currentAddress: employee.currentAddress || '',
        city: employee.city || '',
        state: employee.state || '',
        pincode: employee.pincode || '',
        emergencyContactName: employee.emergencyContactName || '',
        emergencyContactRelation: employee.emergencyContactRelation || '',
        emergencyContactPhone: employee.emergencyContactPhone || '',
        orgId: employee.orgId?.toString() || '',
        locationIds: employee.locationIds?.map(id => id.toString()) || [],
        designation: employee.designation || '',
        department: employee.department || '',
        dateOfJoining: formatDateForInput(employee.dateOfJoining),
        reportingManager: employee.reportingManager || '',
        employmentType: employee.employmentType || 'Full-time',
        status: mapStatus(employee.status),
        probationPeriod: employee.probationPeriod?.toString() || '',
        workShift: employee.workShift || '',
        vendorId: employee.vendorId || '',
        salaryComponents: salaryComps,
        aadharNumber: employee.aadharNumber || '',
        panNumber: employee.panNumber || '',
        bankAccountNumber: employee.bankAccountNumber || '',
        bankIfscCode: employee.bankIfscCode || '',
        bankBranch: employee.bankBranch || '',
        bankName: employee.bankName || '',
        uanNumber: employee.uanNumber || '',
        esiNumber: employee.esiNumber || '',
        shirtSize: employee.shirtSize || '',
        trouserSize: employee.trouserSize || '',
        shoeSize: employee.shoeSize || '',
        profilePhoto: [],
        aadharCardDoc: [],
        panCardDoc: [],
        addressProofDoc: [],
        mergedDetails: [],
      });

      // Set enabled components and show salary section if there are existing components
      if (salaryComps.length > 0) {
        setShowSalarySection(true);
        setEnabledComponents(new Set(salaryComps.map(c => c.component)));
      } else {
        setShowSalarySection(false);
        setEnabledComponents(new Set());
      }

      // Reset unsaved changes flag when loading employee data
      setHasUnsavedChanges(false);
    }
  }, [employee]);

  const loadOrganizationsAndLocations = async () => {
    try {
      const [orgsResponse, locsResponse] = await Promise.all([
        getOrganizations(undefined, { page: 1, limit: 100 }),
        getAllLocations(undefined, { page: 1, limit: 500 })
      ]);

      if (orgsResponse.success && orgsResponse.data) {
        setOrganizations(orgsResponse.data);
      }

      if (locsResponse.success && locsResponse.data) {
        setLocations(locsResponse.data);
      }
    } catch (err) {
      console.error('Error loading organizations and locations:', err);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setHasUnsavedChanges(true);
  };

  const handleLocationToggle = (locationId: number) => {
    const locationIdStr = locationId.toString();
    setFormData((prev) => {
      const newLocationIds = prev.locationIds.includes(locationIdStr)
        ? prev.locationIds.filter((id) => id !== locationIdStr)
        : [...prev.locationIds, locationIdStr];
      return { ...prev, locationIds: newLocationIds };
    });
    setHasUnsavedChanges(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files.length > 0) {
      // Convert FileList to array and append to existing files
      const newFiles = Array.from(files);
      setFormData((prev) => ({
        ...prev,
        [name]: [...prev[name as keyof typeof prev] as File[], ...newFiles],
      }));
      setHasUnsavedChanges(true);
      // Reset the input so the same file can be selected again
      e.target.value = '';
    }
  };

  // Remove a specific new file from the array (before saving)
  const handleRemoveNewFile = (documentType: 'profilePhoto' | 'aadharCardDoc' | 'panCardDoc' | 'addressProofDoc' | 'mergedDetails', index: number) => {
    setFormData((prev) => ({
      ...prev,
      [documentType]: prev[documentType].filter((_, i) => i !== index),
    }));
  };

  const handleToggleComponent = (component: SalaryComponent['component']) => {
    const newEnabled = new Set(enabledComponents);

    if (newEnabled.has(component)) {
      // Remove component
      newEnabled.delete(component);
      setFormData((prev) => ({
        ...prev,
        salaryComponents: prev.salaryComponents.filter(c => c.component !== component),
      }));
    } else {
      // Add component
      newEnabled.add(component);
      setFormData((prev) => ({
        ...prev,
        salaryComponents: [...prev.salaryComponents, { component, amount: 0 }],
      }));
    }

    setEnabledComponents(newEnabled);
    setHasUnsavedChanges(true);
  };

  const handleSalaryAmountChange = (component: SalaryComponent['component'], amount: number) => {
    setFormData((prev) => ({
      ...prev,
      salaryComponents: prev.salaryComponents.map(c =>
        c.component === component ? { ...c, amount } : c
      ),
    }));
    setHasUnsavedChanges(true);
  };

  const getSalaryAmount = (component: SalaryComponent['component']): number => {
    const comp = formData.salaryComponents.find(c => c.component === component);
    return comp?.amount || 0;
  };

  const handleViewDocument = async (
    documentType: 'profilePhoto' | 'aadharCardDoc' | 'panCardDoc' | 'addressProofDoc' | 'mergedDetails',
    s3Key: string
  ) => {
    if (!employee) return;

    try {
      if (!s3Key) {
        alert('Document not found. Please upload the document first.');
        return;
      }

      setLoading(true);

      // Get presigned download URL from Lambda
      const response = await getEmployeeDocDownloadUrl(
        employee.orgId.toString(),
        employee.consoleEmployeeId.toString(),
        s3Key
      );

      console.log('Download URL response:', response);
      console.log('Response data:', response.data);

      if (response.success && response.data) {
        // Extract the actual data (handle nested structure from Lambda response)
        const downloadData = (response.data as any).data || response.data;
        console.log('Download data:', downloadData);
        console.log('Download URL:', downloadData.downloadUrl);

        if (downloadData.downloadUrl) {
          // Open the document in a new tab
          window.open(downloadData.downloadUrl, '_blank');
        } else {
          alert('Failed to load document. Please try again.');
        }
      } else {
        alert('Failed to load document. Please try again.');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      alert('An error occurred while loading the document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (
    documentType: 'profilePhoto' | 'aadharCardDoc' | 'panCardDoc' | 'addressProofDoc' | 'mergedDetails',
    s3Key: string
  ) => {
    if (!employee) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this document? This action cannot be undone.');
    if (!confirmDelete) return;

    try {
      setLoading(true);

      // Get current documents array and filter out the one with matching s3Key
      const currentDocs = employee.documents?.[documentType] || [];
      const updatedDocs = Array.isArray(currentDocs)
        ? currentDocs.filter((doc: any) => doc.s3Key !== s3Key)
        : [];

      // Prepare update payload with the filtered array
      const updatePayload: any = {
        documents: {
          [documentType]: updatedDocs
        }
      };

      const response = await updateEmployee(employee.consoleEmployeeId.toString(), updatePayload);

      if (response.success) {
        alert('Document deleted successfully');
        onSuccess(); // Reload employee data
        onClose();
      } else {
        alert('Failed to delete document. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('An error occurred while deleting the document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAppointmentLetter = () => {
    if (!employee) return;

    // Validate employee data
    const validation = validateEmployeeForAppointmentLetter(employee);

    if (!validation.isValid) {
      setValidationErrors(validation.missingFields);
      setShowValidationModal(true);
      return;
    }

    // Get organization name
    const org = organizations.find(o => o.orgId === employee.orgId);
    const organizationName = org?.name || 'Organization';

    // Get location names for office designation
    const locationNames = employee.locationNames?.map(l => l.name).filter(Boolean).join(', ') || '';

    // Generate appointment letter
    generateAppointmentLetter({
      employee,
      organizationName,
      officeName: locationNames
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee?.consoleEmployeeId || !employee?.orgId) return;

    setLoading(true);
    setError(null);

    try {
      // Step 1: Upload any new documents to S3
      const documentUpdates: any = {};

      const documentMap = {
        profilePhoto: 'profile_photo' as const,
        aadharCardDoc: 'aadhar_card' as const,
        panCardDoc: 'pan_card' as const,
        addressProofDoc: 'address_proof' as const,
        mergedDetails: 'merged_details' as const,
      };

      for (const [fieldName, docType] of Object.entries(documentMap)) {
        const files = formData[fieldName as keyof typeof formData] as File[];

        // Get existing documents for this field
        const existingDocs = employee?.documents?.[fieldName as keyof typeof employee.documents] || [];
        const existingDocsArray = Array.isArray(existingDocs) ? existingDocs : (existingDocs ? [existingDocs] : []);

        // Array to store all document metadata (existing + new)
        const allDocs: any[] = [...existingDocsArray];

        // Upload each new file
        if (files && files.length > 0) {
          for (const file of files) {
            try {
              // Get presigned upload URL
              const uploadUrlResponse = await getEmployeeDocUploadUrl(
                employee.orgId.toString(),
                employee.consoleEmployeeId.toString(),
                docType,
                file.name,
                file.type,
                file.size
              );

              if (!uploadUrlResponse.success || !uploadUrlResponse.data) {
                throw new Error(`Failed to get upload URL for ${fieldName}`);
              }

              // Extract the actual data (handle nested structure from Lambda response)
              const uploadData = (uploadUrlResponse.data as any).data || uploadUrlResponse.data;

              if (!uploadData.uploadUrl || !uploadData.formData) {
                throw new Error(`Invalid upload URL response for ${fieldName}`);
              }

              // Upload file to S3
              const uploadResult = await uploadFileToS3(
                uploadData.uploadUrl,
                uploadData.formData,
                file
              );

              if (!uploadResult.success) {
                throw new Error(`Failed to upload ${fieldName}: ${uploadResult.error}`);
              }

              // Add new document metadata to the array
              allDocs.push({
                fileName: file.name,
                s3Key: uploadData.s3Key,
                fileType: file.type,
                fileSize: file.size,
                uploadedAt: new Date().toISOString(),
              });
            } catch (uploadError) {
              console.error(`Error uploading ${fieldName}:`, uploadError);
              throw new Error(`Failed to upload ${fieldName}: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
            }
          }
        }

        // Only update if there are documents (new or existing)
        if (allDocs.length > 0) {
          documentUpdates[fieldName] = allDocs;
        }
      }

      // Step 2: Update employee record with all data including document metadata
      const updatePayload: any = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        mobile_number: formData.mobileNumber.trim() || null,
        dateOfBirth: formData.dateOfBirth || null,
        gender: formData.gender || null,
        bloodGroup: formData.bloodGroup || null,
        maritalStatus: formData.maritalStatus || null,
        permanentAddress: formData.permanentAddress.trim() || null,
        currentAddress: formData.currentAddress.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        pincode: formData.pincode.trim() || null,
        emergencyContactName: formData.emergencyContactName.trim() || null,
        emergencyContactRelation: formData.emergencyContactRelation.trim() || null,
        emergencyContactPhone: formData.emergencyContactPhone.trim() || null,
        orgId: formData.orgId ? parseInt(formData.orgId) : null,
        locationIds: formData.locationIds.length > 0 ? formData.locationIds.map(id => parseInt(id)) : null,
        employee_id: formData.employeeId.trim() || null,
        designation: formData.designation.trim() || null,
        department: formData.department.trim() || null,
        dateOfJoining: formData.dateOfJoining || null,
        reportingManager: formData.reportingManager.trim() || null,
        employment_type: formData.employmentType,
        status: formData.status,
        probationPeriod: formData.probationPeriod ? parseInt(formData.probationPeriod) : null,
        workShift: formData.workShift.trim() || null,
        vendor_id: formData.vendorId.trim() || null,
        salaryComponents: formData.salaryComponents.length > 0 ? formData.salaryComponents : null,
        aadharNumber: formData.aadharNumber.trim() || null,
        panNumber: formData.panNumber.trim() || null,
        bankAccountNumber: formData.bankAccountNumber.trim() || null,
        bankIfscCode: formData.bankIfscCode.trim() || null,
        bankBranch: formData.bankBranch.trim() || null,
        bankName: formData.bankName.trim() || null,
        uanNumber: formData.uanNumber.trim() || null,
        esiNumber: formData.esiNumber.trim() || null,
        shirtSize: formData.shirtSize.trim() || null,
        trouserSize: formData.trouserSize.trim() || null,
        shoeSize: formData.shoeSize.trim() || null,
      };

      // Add document metadata if any were uploaded
      if (Object.keys(documentUpdates).length > 0) {
        updatePayload.documents = documentUpdates;
      }

      const response = await updateEmployee(employee.consoleEmployeeId.toString(), updatePayload);

      if (response.success) {
        setHasUnsavedChanges(false);
        onSuccess();
        onClose();
      } else {
        setError(response.error || 'Failed to update employee');
      }
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const totalSalary = formData.salaryComponents.reduce((sum, c) => sum + c.amount, 0);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Edit Employee</Title>
          <CloseButton onClick={handleClose}>
            <FiX />
          </CloseButton>
        </Header>

        <Content>
          <Form onSubmit={handleSubmit}>
            {error && <ErrorText>{error}</ErrorText>}

            {/* Basic Information */}
            <SectionTitle>Basic Information</SectionTitle>

            <FormRow>
              <FormGroup>
                <Label>Full Name <span className="required">*</span></Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Employee ID</Label>
                <Input
                  type="text"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleInputChange}
                  placeholder="Auto-generated"
                  disabled
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Mobile Number</Label>
                <Input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  maxLength={10}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Gender</Label>
                <Select name="gender" value={formData.gender} onChange={handleInputChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Blood Group</Label>
                <Select name="bloodGroup" value={formData.bloodGroup} onChange={handleInputChange}>
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Marital Status</Label>
                <Select name="maritalStatus" value={formData.maritalStatus} onChange={handleInputChange}>
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </Select>
              </FormGroup>
            </FormRow>

            {/* Address Information */}
            <SectionTitle>Address Information</SectionTitle>

            <FormGroup>
              <Label>Permanent Address</Label>
              <TextArea
                name="permanentAddress"
                value={formData.permanentAddress}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>Current Address</Label>
              <TextArea
                name="currentAddress"
                value={formData.currentAddress}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>City</Label>
                <Input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>State</Label>
                <Input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Pincode</Label>
              <Input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                maxLength={6}
              />
            </FormGroup>

            {/* Emergency Contact */}
            <SectionTitle>Emergency Contact</SectionTitle>

            <FormRow>
              <FormGroup>
                <Label>Contact Name</Label>
                <Input
                  type="text"
                  name="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Relation</Label>
                <Input
                  type="text"
                  name="emergencyContactRelation"
                  value={formData.emergencyContactRelation}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Contact Phone</Label>
              <Input
                type="tel"
                name="emergencyContactPhone"
                value={formData.emergencyContactPhone}
                onChange={handleInputChange}
                maxLength={10}
              />
            </FormGroup>

            {/* Employment Details */}
            <SectionTitle>Employment Details</SectionTitle>

            <FormRow>
              <FormGroup>
                <Label>Organization <span className="required">*</span></Label>
                <Select name="orgId" value={formData.orgId} onChange={handleInputChange} required>
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.orgId} value={org.orgId}>
                      {org.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Locations <span className="required">*</span></Label>
                <LocationCheckboxContainer>
                  {filteredLocations.length === 0 && formData.orgId && (
                    <div style={{ fontSize: '13px', color: '#666', padding: '8px' }}>
                      No locations available for selected organization
                    </div>
                  )}
                  {filteredLocations.length === 0 && !formData.orgId && (
                    <div style={{ fontSize: '13px', color: '#666', padding: '8px' }}>
                      Please select an organization first
                    </div>
                  )}
                  {filteredLocations.map((location) => (
                    <LocationCheckbox key={location.locationId}>
                      <input
                        type="checkbox"
                        id={`loc-${location.locationId}`}
                        checked={location.locationId ? formData.locationIds.includes(location.locationId.toString()) : false}
                        onChange={() => location.locationId && handleLocationToggle(location.locationId)}
                      />
                      <label htmlFor={`loc-${location.locationId}`}>{location.name}</label>
                    </LocationCheckbox>
                  ))}
                </LocationCheckboxContainer>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Designation</Label>
                <Input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Department</Label>
                <Input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Date of Joining</Label>
                <Input
                  type="date"
                  name="dateOfJoining"
                  value={formData.dateOfJoining}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Reporting Manager</Label>
                <Input
                  type="text"
                  name="reportingManager"
                  value={formData.reportingManager}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Employment Type</Label>
                <Select name="employmentType" value={formData.employmentType} onChange={handleInputChange}>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Intern">Intern</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Status</Label>
                <Select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="terminated">Terminated</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Probation Period (months)</Label>
                <Input
                  type="number"
                  name="probationPeriod"
                  value={formData.probationPeriod}
                  onChange={handleInputChange}
                  min="0"
                  max="24"
                />
              </FormGroup>

              <FormGroup>
                <Label>Work Shift</Label>
                <Input
                  type="text"
                  name="workShift"
                  value={formData.workShift}
                  onChange={handleInputChange}
                  placeholder="e.g., 9 AM - 6 PM"
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Vendor ID</Label>
              <Input
                type="text"
                name="vendorId"
                value={formData.vendorId}
                onChange={handleInputChange}
              />
            </FormGroup>

            {/* Salary Components */}
            {!showSalarySection ? (
              <AddSalaryButton type="button" onClick={() => setShowSalarySection(true)}>
                + Add Salary Information
              </AddSalaryButton>
            ) : (
              <>
                <SectionTitle>Salary Components</SectionTitle>

                <SalaryComponentsGrid>
                  {SALARY_COMPONENT_TYPES.map((component) => (
                    <ComponentCheckbox key={component}>
                      <div>
                        <input
                          type="checkbox"
                          id={`salary-${component}`}
                          checked={enabledComponents.has(component)}
                          onChange={() => handleToggleComponent(component)}
                        />
                        <label htmlFor={`salary-${component}`}>{component}</label>
                      </div>
                      {enabledComponents.has(component) && (
                        <AmountInput
                          type="number"
                          min="0"
                          value={getSalaryAmount(component) || ''}
                          onChange={(e) =>
                            handleSalaryAmountChange(component, Number(e.target.value) || 0)
                          }
                          placeholder="Amount (₹)"
                        />
                      )}
                    </ComponentCheckbox>
                  ))}
                </SalaryComponentsGrid>

                {enabledComponents.size > 0 && (
                  <TotalSalaryDisplay>
                    <strong>Total Salary:</strong> ₹{totalSalary.toLocaleString()}
                  </TotalSalaryDisplay>
                )}

                <RemoveSalaryButton
                  type="button"
                  onClick={() => {
                    setShowSalarySection(false);
                    setEnabledComponents(new Set());
                    setFormData(prev => ({ ...prev, salaryComponents: [] }));
                  }}
                >
                  Remove Salary Information
                </RemoveSalaryButton>
              </>
            )}

            {/* Identity & Banking Details */}
            <SectionTitle>Identity & Banking Details</SectionTitle>

            <FormRow>
              <FormGroup>
                <Label>Aadhar Number</Label>
                <Input
                  type="text"
                  name="aadharNumber"
                  value={formData.aadharNumber}
                  onChange={handleInputChange}
                  maxLength={12}
                  placeholder="12-digit Aadhar number"
                />
              </FormGroup>

              <FormGroup>
                <Label>PAN Number</Label>
                <Input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleInputChange}
                  maxLength={10}
                  placeholder="10-character PAN"
                  style={{ textTransform: 'uppercase' }}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>UAN Number</Label>
                <Input
                  type="text"
                  name="uanNumber"
                  value={formData.uanNumber}
                  onChange={handleInputChange}
                  placeholder="Universal Account Number"
                />
              </FormGroup>

              <FormGroup>
                <Label>ESI Number</Label>
                <Input
                  type="text"
                  name="esiNumber"
                  value={formData.esiNumber}
                  onChange={handleInputChange}
                  placeholder="Employee State Insurance Number"
                />
              </FormGroup>
            </FormRow>

            <SectionTitle>Uniform Information</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>Shirt Size</Label>
                <Input
                  type="text"
                  name="shirtSize"
                  value={formData.shirtSize}
                  onChange={handleInputChange}
                  placeholder="e.g., S, M, L, XL"
                />
              </FormGroup>

              <FormGroup>
                <Label>Trouser Size</Label>
                <Input
                  type="text"
                  name="trouserSize"
                  value={formData.trouserSize}
                  onChange={handleInputChange}
                  placeholder="e.g., 28, 30, 32, 34"
                />
              </FormGroup>

              <FormGroup>
                <Label>Shoe Size</Label>
                <Input
                  type="text"
                  name="shoeSize"
                  value={formData.shoeSize}
                  onChange={handleInputChange}
                  placeholder="e.g., 7, 8, 9, 10"
                />
              </FormGroup>
            </FormRow>

            <SectionTitle>Banking Information</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>Bank Name</Label>
                <Input
                  type="text"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Account Number</Label>
                <Input
                  type="text"
                  name="bankAccountNumber"
                  value={formData.bankAccountNumber}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>IFSC Code</Label>
                <Input
                  type="text"
                  name="bankIfscCode"
                  value={formData.bankIfscCode}
                  onChange={handleInputChange}
                  maxLength={11}
                  style={{ textTransform: 'uppercase' }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Branch Name</Label>
                <Input
                  type="text"
                  name="bankBranch"
                  value={formData.bankBranch}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormRow>

            {/* Document Upload */}
            <SectionTitle>Documents</SectionTitle>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
              Upload or update employee documents. Supported formats: PDF, JPG, PNG (Max 5MB per file)
            </p>

            <FormRow>
              <FormGroup>
                <Label>Profile Photo</Label>
                <DocumentUploadWrapper>
                  <FileInput type="file" name="profilePhoto" onChange={handleFileChange} accept="image/*" multiple />

                  {/* Show new files (not yet saved) */}
                  {formData.profilePhoto.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>New files (Click Save to upload):</FileInfo>
                      {formData.profilePhoto.map((file, index) => (
                        <DocumentActions key={`new-${index}`}>
                          <FileInfo>📎 {file.name}</FileInfo>
                          <DeleteDocButton type="button" onClick={() => handleRemoveNewFile('profilePhoto', index)}>
                            <FiTrash2 /> Remove
                          </DeleteDocButton>
                        </DocumentActions>
                      ))}
                    </div>
                  )}

                  {/* Show existing saved files */}
                  {(() => {
                    const docs = employee?.documents?.profilePhoto;
                    const docsArray = docs ? (Array.isArray(docs) ? docs : [docs]) : [];
                    return docsArray.length > 0 ? (
                      <div style={{ marginTop: '8px' }}>
                        <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>Uploaded files:</FileInfo>
                        {docsArray.map((doc: any, index: number) => (
                          <DocumentActions key={`existing-${index}`}>
                            <FileInfo>📄 {doc.fileName}</FileInfo>
                            <DownloadDocButton type="button" onClick={() => handleViewDocument('profilePhoto', doc.s3Key)}>
                              <FiDownload /> Download
                            </DownloadDocButton>
                            <DeleteDocButton type="button" onClick={() => handleDeleteDocument('profilePhoto', doc.s3Key)}>
                              <FiTrash2 /> Delete
                            </DeleteDocButton>
                          </DocumentActions>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </DocumentUploadWrapper>
              </FormGroup>

              <FormGroup>
                <Label>Aadhar Card</Label>
                <DocumentUploadWrapper>
                  <FileInput type="file" name="aadharCardDoc" onChange={handleFileChange} accept=".pdf,image/*" multiple />

                  {/* Show new files (not yet saved) */}
                  {formData.aadharCardDoc.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>New files (Click Save to upload):</FileInfo>
                      {formData.aadharCardDoc.map((file, index) => (
                        <DocumentActions key={`new-${index}`}>
                          <FileInfo>📎 {file.name}</FileInfo>
                          <DeleteDocButton type="button" onClick={() => handleRemoveNewFile('aadharCardDoc', index)}>
                            <FiTrash2 /> Remove
                          </DeleteDocButton>
                        </DocumentActions>
                      ))}
                    </div>
                  )}

                  {/* Show existing saved files */}
                  {(() => {
                    const docs = employee?.documents?.aadharCardDoc;
                    const docsArray = docs ? (Array.isArray(docs) ? docs : [docs]) : [];
                    return docsArray.length > 0 ? (
                      <div style={{ marginTop: '8px' }}>
                        <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>Uploaded files:</FileInfo>
                        {docsArray.map((doc: any, index: number) => (
                          <DocumentActions key={`existing-${index}`}>
                            <FileInfo>📄 {doc.fileName}</FileInfo>
                            <DownloadDocButton type="button" onClick={() => handleViewDocument('aadharCardDoc', doc.s3Key)}>
                              <FiDownload /> Download
                            </DownloadDocButton>
                            <DeleteDocButton type="button" onClick={() => handleDeleteDocument('aadharCardDoc', doc.s3Key)}>
                              <FiTrash2 /> Delete
                            </DeleteDocButton>
                          </DocumentActions>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </DocumentUploadWrapper>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>PAN Card</Label>
                <DocumentUploadWrapper>
                  <FileInput type="file" name="panCardDoc" onChange={handleFileChange} accept=".pdf,image/*" multiple />

                  {/* Show new files (not yet saved) */}
                  {formData.panCardDoc.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>New files (Click Save to upload):</FileInfo>
                      {formData.panCardDoc.map((file, index) => (
                        <DocumentActions key={`new-${index}`}>
                          <FileInfo>📎 {file.name}</FileInfo>
                          <DeleteDocButton type="button" onClick={() => handleRemoveNewFile('panCardDoc', index)}>
                            <FiTrash2 /> Remove
                          </DeleteDocButton>
                        </DocumentActions>
                      ))}
                    </div>
                  )}

                  {/* Show existing saved files */}
                  {(() => {
                    const docs = employee?.documents?.panCardDoc;
                    const docsArray = docs ? (Array.isArray(docs) ? docs : [docs]) : [];
                    return docsArray.length > 0 ? (
                      <div style={{ marginTop: '8px' }}>
                        <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>Uploaded files:</FileInfo>
                        {docsArray.map((doc: any, index: number) => (
                          <DocumentActions key={`existing-${index}`}>
                            <FileInfo>📄 {doc.fileName}</FileInfo>
                            <DownloadDocButton type="button" onClick={() => handleViewDocument('panCardDoc', doc.s3Key)}>
                              <FiDownload /> Download
                            </DownloadDocButton>
                            <DeleteDocButton type="button" onClick={() => handleDeleteDocument('panCardDoc', doc.s3Key)}>
                              <FiTrash2 /> Delete
                            </DeleteDocButton>
                          </DocumentActions>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </DocumentUploadWrapper>
              </FormGroup>

              <FormGroup>
                <Label>Address Proof</Label>
                <DocumentUploadWrapper>
                  <FileInput type="file" name="addressProofDoc" onChange={handleFileChange} accept=".pdf,image/*" multiple />

                  {/* Show new files (not yet saved) */}
                  {formData.addressProofDoc.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>New files (Click Save to upload):</FileInfo>
                      {formData.addressProofDoc.map((file, index) => (
                        <DocumentActions key={`new-${index}`}>
                          <FileInfo>📎 {file.name}</FileInfo>
                          <DeleteDocButton type="button" onClick={() => handleRemoveNewFile('addressProofDoc', index)}>
                            <FiTrash2 /> Remove
                          </DeleteDocButton>
                        </DocumentActions>
                      ))}
                    </div>
                  )}

                  {/* Show existing saved files */}
                  {(() => {
                    const docs = employee?.documents?.addressProofDoc;
                    const docsArray = docs ? (Array.isArray(docs) ? docs : [docs]) : [];
                    return docsArray.length > 0 ? (
                      <div style={{ marginTop: '8px' }}>
                        <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>Uploaded files:</FileInfo>
                        {docsArray.map((doc: any, index: number) => (
                          <DocumentActions key={`existing-${index}`}>
                            <FileInfo>📄 {doc.fileName}</FileInfo>
                            <DownloadDocButton type="button" onClick={() => handleViewDocument('addressProofDoc', doc.s3Key)}>
                              <FiDownload /> Download
                            </DownloadDocButton>
                            <DeleteDocButton type="button" onClick={() => handleDeleteDocument('addressProofDoc', doc.s3Key)}>
                              <FiTrash2 /> Delete
                            </DeleteDocButton>
                          </DocumentActions>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </DocumentUploadWrapper>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Merged Details</Label>
              <DocumentUploadWrapper>
                <FileInput type="file" name="mergedDetails" onChange={handleFileChange} accept=".pdf,image/*" multiple />

                {/* Show new files (not yet saved) */}
                {formData.mergedDetails.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>New files (Click Save to upload):</FileInfo>
                    {formData.mergedDetails.map((file, index) => (
                      <DocumentActions key={`new-${index}`}>
                        <FileInfo>📎 {file.name}</FileInfo>
                        <DeleteDocButton type="button" onClick={() => handleRemoveNewFile('mergedDetails', index)}>
                          <FiTrash2 /> Remove
                        </DeleteDocButton>
                      </DocumentActions>
                    ))}
                  </div>
                )}

                {/* Show existing saved files */}
                {(() => {
                  const docs = employee?.documents?.mergedDetails;
                  const docsArray = docs ? (Array.isArray(docs) ? docs : [docs]) : [];
                  return docsArray.length > 0 ? (
                    <div style={{ marginTop: '8px' }}>
                      <FileInfo style={{ marginBottom: '4px', fontWeight: 'bold' }}>Uploaded files:</FileInfo>
                      {docsArray.map((doc: any, index: number) => (
                        <DocumentActions key={`existing-${index}`}>
                          <FileInfo>📄 {doc.fileName}</FileInfo>
                          <DownloadDocButton type="button" onClick={() => handleViewDocument('mergedDetails', doc.s3Key)}>
                            <FiDownload /> Download
                          </DownloadDocButton>
                          <DeleteDocButton type="button" onClick={() => handleDeleteDocument('mergedDetails', doc.s3Key)}>
                            <FiTrash2 /> Delete
                          </DeleteDocButton>
                        </DocumentActions>
                      ))}
                    </div>
                  ) : null;
                })()}
              </DocumentUploadWrapper>
            </FormGroup>

            <ButtonGroup>
              <DownloadButton type="button" onClick={handleDownloadAppointmentLetter}>
                <FiDownload />
                Download Appointment Letter
              </DownloadButton>
              <ButtonGroupRight>
                <CancelButton type="button" onClick={handleClose} disabled={loading}>
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </SubmitButton>
              </ButtonGroupRight>
            </ButtonGroup>
          </Form>
        </Content>
      </Modal>

      <ValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        missingFields={validationErrors}
        title="Cannot Generate Appointment Letter"
      />
    </Overlay>
  );
};

// Styled Components
const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 30px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    font-size: 20px;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};

  &:first-of-type {
    margin-top: 0;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};

  .required {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.2s;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 14px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.error}15;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.colors.error}30;
`;

const AddSalaryButton = styled.button`
  padding: 12px 20px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px dashed ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SalaryComponentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const ComponentCheckbox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  transition: all 0.2s;

  &:has(input:checked) {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}05;
  }

  > div:first-child {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }

  label {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
    user-select: none;
  }
`;

const AmountInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  margin-top: 4px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RemoveSalaryButton = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.error}40;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.error};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.error}10;
    border-color: ${({ theme }) => theme.colors.error};
  }
`;

const TotalSalaryDisplay = styled.div`
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.primary}10;
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;

  strong {
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const ButtonGroupRight = styled.div`
  display: flex;
  gap: 12px;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceLight};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LocationCheckboxContainer = styled.div`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.background};
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LocationCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceLight};
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }

  label {
    flex: 1;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
    user-select: none;
  }
`;

const FileInput = styled.input`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.2s;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::file-selector-button {
    padding: 6px 12px;
    margin-right: 12px;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: ${({ theme }) => theme.colors.primaryDark};
    }
  }
`;

const FileInfo = styled.div`
  margin-top: 6px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.primary}10;
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
  word-break: break-all;
`;

const DocumentUploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DocumentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
`;

const DownloadDocButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark || '#2563eb'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteDocButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #ef4444;
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: #dc2626;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
